/**
 * Date helpers for the asset screens. The API speaks ISO (`date` columns as
 * "YYYY-MM-DD", `timestamptz` as full ISO strings); the UI shows Serbian
 * "dd.mm.yyyy" and feeds `DateInput` / `DateTimeInput` (`components/DateInput.tsx`),
 * which keep the native `<input type="date">` / `datetime-local` value contract
 * ("YYYY-MM-DD" / "YYYY-MM-DDTHH:mm", "" when empty).
 */

const pad = (n: number) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" of a local date (default: today). */
export function toIsoDate(d: Date = new Date()): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today + `days`, as "YYYY-MM-DD". */
export function isoDatePlusDays(days: number, from: Date = new Date()): string {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

/** `DateTimeInput` value (datetime-local contract: local time, minute precision). */
export function toLocalDateTimeInput(d: Date = new Date()): string {
  return `${toIsoDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value → ISO instant; "" → null. */
export function localDateTimeInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** "2026-09-08" | ISO instant → "08.09.2026"; null/invalid → "". */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** ISO instant → "08.09.2026 14:05"; null/invalid → "". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${formatDate(value)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** True when an ISO date ("YYYY-MM-DD") is today or earlier. */
export function isDueDate(value: string | null | undefined, today: string = toIsoDate()): boolean {
  return !!value && value.slice(0, 10) <= today;
}

/**
 * Coarse Serbian relative time for comment lists: "upravo sad", "pre 5 min",
 * "pre 3 h", "pre 2 dana"; anything older than a week falls back to the exact
 * date. The exact timestamp is meant to go into a `title` next to it.
 */
export function formatRelativeSr(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const delta = Math.max(0, now.getTime() - d.getTime());
  const min = Math.round(delta / 60_000);
  if (min < 1) return "upravo sad";
  if (min < 60) return `pre ${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `pre ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return days === 1 ? "juče" : `pre ${days} dana`;
  return formatDate(value);
}

/* ------------------------------------------------------------------------- */
/* Serbian calendar names and the typed-text parsers behind DateInput.       */
/* ------------------------------------------------------------------------- */

/** Month names in Serbian Latin, lowercase (index 0 = januar). */
export const SR_MONTHS = [
  "januar", "februar", "mart", "april", "maj", "jun",
  "jul", "avgust", "septembar", "oktobar", "novembar", "decembar",
] as const;

/** Weekday abbreviations starting with Monday — the calendar's column order. */
export const SR_WEEKDAYS_SHORT = ["Pon", "Uto", "Sre", "Čet", "Pet", "Sub", "Ned"] as const;

/** "Septembar 2026" — month title with a capital first letter. */
export function srMonthTitle(year: number, monthIndex: number): string {
  const m = SR_MONTHS[monthIndex];
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${year}`;
}

/** True when y-m-d is a real calendar date (rejects 31.02., month 13, year < 1000…). */
export function isValidYmd(y: number, m: number, d: number): boolean {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1) return false;
  return d <= daysInMonth(y, m - 1);
}

/** Number of days in a month (monthIndex 0–11). */
export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** "YYYY-MM-DD" → "dd.mm.yyyy"; anything else → "". */
export function formatSrDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

/** "YYYY-MM-DDTHH:mm" → "dd.mm.yyyy HH:mm" (24 h); anything else → "". */
export function formatSrDateTime(value: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(value);
  return m ? `${formatSrDate(m[1])} ${m[2]}:${m[3]}` : "";
}

const SR_DATE_RE = /^\s*(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{4})\s*\.?\s*$/;
const SR_DATE_TIME_RE = /^\s*(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{4})\s*\.?(?:\s+(\d{1,2})[:.](\d{2}))?\s*$/;

/**
 * Typed Serbian date → "YYYY-MM-DD". Accepts "5.3.2026", "05.03.2026",
 * "05.03.2026." (trailing dot) and spaces around the dots. Empty text → "";
 * anything unparsable or not a real date → null.
 */
export function parseSrDate(text: string): string | null {
  if (text.trim() === "") return "";
  const m = SR_DATE_RE.exec(text);
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]);
  const y = Number(m[3]);
  if (!isValidYmd(y, mo, d)) return null;
  return `${y}-${pad(mo)}-${pad(d)}`;
}

/**
 * Typed Serbian date-time → "YYYY-MM-DDTHH:mm" (24 h). Accepts "5.3.2026 7:05",
 * "05.03.2026 18:33"; a bare date means midnight. Empty text → ""; invalid → null.
 */
export function parseSrDateTime(text: string): string | null {
  if (text.trim() === "") return "";
  const m = SR_DATE_TIME_RE.exec(text);
  if (!m) return null;
  const date = parseSrDate(`${m[1]}.${m[2]}.${m[3]}`);
  if (!date) return null;
  const h = m[4] === undefined ? 0 : Number(m[4]);
  const mi = m[5] === undefined ? 0 : Number(m[5]);
  if (h > 23 || mi > 59) return null;
  return `${date}T${pad(h)}:${pad(mi)}`;
}
